'use client'

import { useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'

import { Bus, CalendarDays, LoaderCircle, LocateFixed, Map as MapIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { usePlacesAutocomplete, type PlacePrediction } from '@/hooks/use-places-autocomplete'
import { faires, stateNames } from '@/content/renaissance-faires/data'
import type { Season } from '@/content/renaissance-faires/types'

import { formatDistance } from './distance'
import {
    findFaires,
    resultSummary,
    type FinderOrigin,
    type FinderResult,
    type SortMode,
} from './finder-state'
import { SectionHeading } from './heraldry'

const FairesMap = dynamic(() => import('./faires-map').then((m) => m.FairesMap), {
    ssr: false,
    loading: () => (
        <div className="flex h-full w-full items-center justify-center bg-[var(--rf-smoke-50)] text-sm text-[var(--rf-fg-3)]">
            Loading map…
        </div>
    ),
})

/** How many results render before "show more". */
const PAGE_SIZE = 20

/** Ties the combobox input to its listbox and the active option (ARIA). */
const SUGGESTIONS_ID = 'rf-location-suggestions'

const SEASONS: { label: string; value: Season | null }[] = [
    { label: 'All seasons', value: null },
    { label: 'Spring', value: 'spring' },
    { label: 'Summer', value: 'summer' },
    { label: 'Fall', value: 'fall' },
    { label: 'Winter', value: 'winter' },
]

const SORTS: { label: string; value: SortMode; needsOrigin?: boolean }[] = [
    { label: 'Coming up', value: 'soonest' },
    { label: 'Nearest', value: 'nearest', needsOrigin: true },
    { label: 'A–Z', value: 'state' },
]

const SEASON_STYLES: Record<Season, string> = {
    spring: 'bg-[#fcf3ca] text-[#876102]',
    summer: 'bg-[#eef7d6] text-[#4e6b00]',
    fall: 'bg-[#ece6fa] text-[#523a9e]',
    winter: 'bg-[#e6f9fb] text-[#00606b]',
}

const TONE_COLORS = {
    running: 'text-[var(--rf-success)]',
    upcoming: 'text-[var(--rf-fg)]',
    projected: 'text-[var(--rf-fg-3)]',
    unposted: 'text-[var(--rf-fg-2)]',
} as const

const CONFIDENCE_LABELS = {
    official: "Dates verified on the faire's site",
    secondary: 'Dates from a directory — not yet confirmed',
    unposted: 'Dates not posted yet',
} as const

/** Only US places can hold a US faire, so foreign ZIP collisions are filtered out. */
function isUnitedStates(prediction: PlacePrediction): boolean {
    return /,\s*USA$/.test(prediction.description.trim())
}

const chipClass = (active: boolean, disabled = false) =>
    cn(
        'h-8 cursor-pointer rounded-[2px] border px-3 font-sans text-[13px] font-medium transition-colors duration-150',
        disabled && 'cursor-default border-[var(--rf-border)] text-[var(--rf-fg-3)] opacity-60',
        !disabled && active && 'border-[var(--rf-blu-700)] bg-[var(--rf-blu-700)] text-white',
        !disabled &&
            !active &&
            'border-[var(--rf-border-dark)] bg-white text-[var(--rf-fg-2)] hover:bg-[var(--rf-smoke-25)]',
    )

interface FaireFinderProps {
    /**
     * Server-computed "today" (`YYYY-MM-DD`). Every date line and the whole
     * soonest-first sort derive from this, never the browser clock, so the
     * SSR'd list and the first client render agree
     * (invariants.md#ssr-safe-rendered-dates).
     */
    today: string
}

/**
 * The finder: filters, a crawlable result list, and the map beside it.
 *
 * The list is server-rendered on first paint (this island SSRs), so every faire
 * name, its dates and its trip link are in the HTML for crawlers even though the
 * filtering is client-side. The map is `ssr: false` — Leaflet touches `window`
 * at import — and is purely an alternate view of the same list.
 */
export const FaireFinder = ({ today }: FaireFinderProps) => {
    const [season, setSeason] = useState<Season | null>(null)
    const [state, setState] = useState<string | null>(null)
    const [sort, setSort] = useState<SortMode>('soonest')
    const [origin, setOrigin] = useState<FinderOrigin | null>(null)
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const [activeId, setActiveId] = useState<string | null>(null)
    const [focusRequest, setFocusRequest] = useState<{ id: string; nonce: number } | null>(null)
    const [mapOpen, setMapOpen] = useState(true)
    const [locating, setLocating] = useState(false)
    const [resolving, setResolving] = useState(false)
    const [locationError, setLocationError] = useState<string | null>(null)

    const [query, setQuery] = useState('')
    const [suggestionsOpen, setSuggestionsOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const { predictions: rawPredictions } = usePlacesAutocomplete(query)
    const predictions = useMemo(() => rawPredictions.filter(isUnitedStates), [rawPredictions])
    const showSuggestions = suggestionsOpen && query.trim().length >= 2 && predictions.length > 0

    const rowRefs = useRef<Record<string, HTMLLIElement | null>>({})
    const listRef = useRef<HTMLDivElement>(null)

    const filters = useMemo(() => ({ season, state, origin, sort }), [season, state, origin, sort])
    const results = useMemo(() => findFaires(faires, filters, today), [filters, today])
    const visible = results.slice(0, visibleCount)

    const stateOptions = useMemo(
        () =>
            Object.entries(stateNames)
                .filter(([code]) => faires.some((faire) => faire.state === code))
                .map(([code, name]) => ({ code, name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [],
    )

    const resetPaging = () => {
        setVisibleCount(PAGE_SIZE)
    }

    const applyOrigin = (next: FinderOrigin) => {
        setOrigin(next)
        setSort('nearest')
        setState(null)
        setLocationError(null)
        resetPaging()
    }

    const selectPrediction = async (prediction: PlacePrediction) => {
        setSuggestionsOpen(false)
        setFocusedIndex(-1)
        setQuery(prediction.mainText)
        setResolving(true)
        try {
            const response = await fetch(
                `/api/nexus/places/details?placeId=${encodeURIComponent(prediction.placeId)}`,
            )
            if (!response.ok) {
                throw new Error(`place details ${response.status}`)
            }
            const details = (await response.json()) as { lat: number; lng: number }
            applyOrigin({ lat: details.lat, lng: details.lng, label: prediction.mainText })
        } catch {
            setLocationError("Couldn't place that spot. Try another city or ZIP.")
        } finally {
            setResolving(false)
        }
    }

    const useMyLocation = () => {
        if (locating) {
            return
        }
        setLocationError(null)
        if (!navigator.geolocation) {
            setLocationError("Your browser won't share a location — try typing a city instead.")
            return
        }
        setLocating(true)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocating(false)
                applyOrigin({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    label: 'you',
                })
            },
            () => {
                setLocating(false)
                setLocationError("Couldn't get your location — try typing a city instead.")
            },
            { maximumAge: 300000, timeout: 12000 },
        )
    }

    const clearOrigin = () => {
        setOrigin(null)
        setQuery('')
        setSort('soonest')
        resetPaging()
    }

    /** Row click: highlight, and ask the map to fly there. */
    const selectFromList = (id: string) => {
        setActiveId(id)
        setFocusRequest((previous) => ({ id, nonce: (previous?.nonce ?? 0) + 1 }))
    }

    /** Pin click: highlight and scroll the row into view, but don't move the map. */
    const selectFromPin = (id: string) => {
        setActiveId(id)
        const index = results.findIndex((faire) => faire.id === id)
        if (index >= visibleCount) {
            setVisibleCount(Math.ceil((index + 1) / PAGE_SIZE) * PAGE_SIZE)
        }
        // Deferred so a row revealed by the paging bump above exists to scroll to.
        requestAnimationFrame(() => {
            rowRefs.current[id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        })
    }

    return (
        <section
            id="finder"
            className="mx-auto box-border max-w-[1600px] scroll-mt-4 px-[18px] pt-9 pb-15"
        >
            <SectionHeading className="mb-1.5">Find a faire near you</SectionHeading>
            <p className="m-0 mb-[18px] text-sm text-[var(--rf-fg-2)]">
                Search a city or ZIP, use your location, or browse by season and state.
            </p>

            <div className="mb-[18px] rounded-lg border border-[var(--rf-border)] bg-white p-3 shadow-[var(--rf-shadow-1)]">
                <div className="flex flex-wrap items-stretch gap-2">
                    <div className="relative min-w-[220px] flex-1 basis-[260px]">
                        <input
                            type="text"
                            role="combobox"
                            aria-expanded={showSuggestions}
                            aria-controls={SUGGESTIONS_ID}
                            aria-activedescendant={
                                showSuggestions && focusedIndex >= 0
                                    ? `${SUGGESTIONS_ID}-${focusedIndex}`
                                    : undefined
                            }
                            aria-autocomplete="list"
                            aria-label="Search by city or ZIP"
                            placeholder="City or ZIP — e.g. Austin, or 78701"
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value)
                                setSuggestionsOpen(true)
                                setFocusedIndex(-1)
                                setLocationError(null)
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowDown' && showSuggestions) {
                                    event.preventDefault()
                                    setFocusedIndex((index) =>
                                        Math.min(index + 1, predictions.length - 1),
                                    )
                                } else if (event.key === 'ArrowUp' && showSuggestions) {
                                    event.preventDefault()
                                    setFocusedIndex((index) => Math.max(index - 1, 0))
                                } else if (event.key === 'Enter' && predictions.length > 0) {
                                    event.preventDefault()
                                    // Enter with nothing highlighted commits the
                                    // first suggestion, matching the search widget.
                                    void selectPrediction(predictions[Math.max(focusedIndex, 0)])
                                } else if (event.key === 'Escape') {
                                    setSuggestionsOpen(false)
                                    setFocusedIndex(-1)
                                }
                            }}
                            // Delayed so an option's mousedown wins over blur-close.
                            onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                            className="box-border h-11 w-full rounded-[3px] border border-[var(--rf-border-dark)] px-3 font-sans text-base text-[var(--rf-fg)] outline-none focus:border-[var(--rf-blu-700)] focus:shadow-[var(--rf-ring)]"
                        />
                        {resolving && (
                            <LoaderCircle
                                className="absolute top-3.5 right-3 h-4 w-4 animate-spin text-[var(--rf-fg-3)]"
                                aria-hidden="true"
                            />
                        )}
                        {showSuggestions && (
                            <ul
                                id={SUGGESTIONS_ID}
                                role="listbox"
                                aria-label="Location suggestions"
                                className="absolute top-[46px] right-0 left-0 z-[1200] overflow-hidden rounded-[3px] border border-[var(--rf-border)] bg-white shadow-[var(--rf-shadow-3)]"
                            >
                                {predictions.map((prediction, index) => (
                                    <li
                                        key={prediction.placeId}
                                        id={`${SUGGESTIONS_ID}-${index}`}
                                        role="option"
                                        aria-selected={index === focusedIndex}
                                    >
                                        <button
                                            type="button"
                                            onMouseDown={(event) => {
                                                event.preventDefault()
                                                void selectPrediction(prediction)
                                            }}
                                            className={cn(
                                                'flex w-full items-baseline justify-between gap-3 px-3 py-2.5 text-left font-sans text-sm text-[var(--rf-fg)]',
                                                index === focusedIndex
                                                    ? 'bg-[var(--rf-blu-25)]'
                                                    : 'hover:bg-[var(--rf-blu-25)]',
                                            )}
                                        >
                                            <span className="font-semibold">
                                                {prediction.mainText}
                                            </span>
                                            <span className="truncate text-xs text-[var(--rf-fg-3)]">
                                                {prediction.secondaryText}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={useMyLocation}
                        className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-[3px] border border-[var(--rf-border-dark)] bg-white px-3.5 font-sans text-sm font-semibold text-[var(--rf-fg-2)] transition-colors duration-150 hover:bg-[var(--rf-smoke-25)]"
                    >
                        {locating ? (
                            <LoaderCircle
                                className="h-[18px] w-[18px] animate-spin"
                                aria-hidden="true"
                            />
                        ) : (
                            <LocateFixed className="h-[18px] w-[18px]" aria-hidden="true" />
                        )}
                        {locating ? 'Finding you…' : 'Use My Location'}
                    </button>
                    {origin && (
                        <button
                            type="button"
                            onClick={clearOrigin}
                            className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-[3px] border border-[var(--rf-border)] bg-[var(--rf-smoke-50)] px-3 font-sans text-sm text-[var(--rf-fg-2)] hover:bg-[var(--rf-border-light)]"
                        >
                            Near {origin.label} ✕
                        </button>
                    )}
                </div>
                {locationError && (
                    <p role="alert" className="mt-2 mb-0 text-[13px] text-[var(--rf-warning)]">
                        {locationError}
                    </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div
                        className="flex flex-wrap gap-[3px]"
                        role="group"
                        aria-label="Season filter"
                    >
                        {SEASONS.map((option) => (
                            <button
                                key={option.label}
                                type="button"
                                aria-pressed={season === option.value}
                                onClick={() => {
                                    setSeason(option.value)
                                    resetPaging()
                                }}
                                className={chipClass(season === option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <select
                        value={state ?? ''}
                        aria-label="Filter by state"
                        onChange={(event) => {
                            setState(event.target.value || null)
                            resetPaging()
                        }}
                        className="h-8 max-w-[180px] rounded-[3px] border border-[var(--rf-border-dark)] bg-white px-2 font-sans text-[13px] text-[var(--rf-fg)]"
                    >
                        <option value="">All states</option>
                        {stateOptions.map((option) => (
                            <option key={option.code} value={option.code}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                    <div className="flex gap-[3px] sm:ml-auto" role="group" aria-label="Sort">
                        {SORTS.map((option) => {
                            const disabled = Boolean(option.needsOrigin) && !origin
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    disabled={disabled}
                                    aria-pressed={sort === option.value}
                                    title={
                                        disabled ? 'Search a city or use your location first' : ''
                                    }
                                    onClick={() => {
                                        setSort(option.value)
                                        resetPaging()
                                    }}
                                    className={chipClass(sort === option.value, disabled)}
                                >
                                    {option.label}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>

            <button
                type="button"
                onClick={() => setMapOpen((open) => !open)}
                className="mb-3 inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[3px] bg-[var(--rf-primary)] px-3.5 font-sans text-sm font-bold text-white hover:bg-[var(--rf-primary-hover)] lg:hidden"
            >
                <MapIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                {mapOpen ? 'Hide Map' : 'Show Map'}
            </button>

            {/*
             * The list comes first in the DOM so a crawler reads faire names and
             * trip links before the map's furniture; `order` puts the map back
             * above the list on small screens, where it has to sit directly under
             * the show/hide toggle rather than below 200-odd result rows.
             */}
            <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
                <div
                    ref={listRef}
                    className="rf-result-list order-2 min-w-0 lg:order-1 lg:max-h-[640px] lg:overflow-y-auto lg:pr-1.5"
                >
                    <p className="m-0 mb-2 text-[13px] text-[var(--rf-fg-3)]" aria-live="polite">
                        {resultSummary(results.length, filters, stateNames)}
                    </p>
                    {results.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-[var(--rf-border-dark)] p-6 text-center text-[15px] text-[var(--rf-fg-2)]">
                            No faires match those filters. Try a broader season, another state — or{' '}
                            <a
                                href="#full-list"
                                className="font-semibold text-[var(--rf-blu-700)] underline"
                            >
                                the full list below
                            </a>
                            .
                        </div>
                    ) : (
                        <ul className="m-0 flex list-none flex-col gap-2 p-0">
                            {visible.map((faire) => (
                                <FaireResultRow
                                    key={faire.id}
                                    faire={faire}
                                    active={activeId === faire.id}
                                    onSelect={() => selectFromList(faire.id)}
                                    rowRef={(element) => {
                                        rowRefs.current[faire.id] = element
                                    }}
                                />
                            ))}
                        </ul>
                    )}
                    {results.length > visibleCount && (
                        <button
                            type="button"
                            onClick={() => setVisibleCount((count) => count + 40)}
                            className="mt-3 h-10 w-full cursor-pointer rounded-[3px] border border-[var(--rf-border-dark)] bg-white font-sans text-sm font-semibold text-[var(--rf-fg-2)] hover:bg-[var(--rf-smoke-25)]"
                        >
                            Show {Math.min(40, results.length - visibleCount)} more (
                            {results.length - visibleCount} remaining)
                        </button>
                    )}
                    <p className="mt-3 mb-0 text-center text-sm">
                        <a
                            href="#full-list"
                            className="font-semibold text-[var(--rf-blu-700)] hover:underline"
                        >
                            See the full list ↓
                        </a>
                    </p>
                </div>

                <div className="order-1 lg:sticky lg:top-3 lg:order-2">
                    <div
                        className={cn(
                            'relative h-[340px] overflow-hidden rounded-lg border border-[var(--rf-blu-950)] lg:block lg:h-[640px]',
                            !mapOpen && 'hidden',
                        )}
                    >
                        <FairesMap
                            faires={results}
                            activeId={activeId}
                            focusRequest={focusRequest}
                            onPinSelect={selectFromPin}
                            origin={origin}
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

interface FaireResultRowProps {
    faire: FinderResult
    active: boolean
    onSelect: () => void
    rowRef: (element: HTMLLIElement | null) => void
}

const FaireResultRow = ({ faire, active, onSelect, rowRef }: FaireResultRowProps) => {
    const primarySeason = faire.seasons[0]
    const isTbd = faire.schedule.tone === 'unposted' || faire.dateConfidence === 'unposted'
    return (
        <li ref={rowRef}>
            {/*
             * The row is a map-selection control, and its interactive children
             * (faire site, trip link) are real links — so it's a <button> wrapping
             * content rather than an <a>, and the links stop propagation so a
             * click on them doesn't also re-frame the map.
             */}
            <button
                type="button"
                onClick={onSelect}
                aria-pressed={active}
                className={cn(
                    'w-full cursor-pointer rounded-[3px] border border-l-[3px] bg-white p-3 text-left transition-shadow',
                    active
                        ? 'border-[var(--rf-blu-700)] border-l-[var(--rf-blu-700)] shadow-[var(--rf-shadow-3)]'
                        : cn(
                              'border-[var(--rf-border)]',
                              faire.isFlagship
                                  ? 'border-l-[var(--rf-green)]'
                                  : 'border-l-[var(--rf-border)]',
                          ),
                )}
            >
                <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-base font-semibold text-[var(--rf-blu-700)]">
                        {faire.name}
                    </span>
                    {faire.isFlagship && (
                        <span className="rounded-[2px] border border-[var(--rf-blu-100)] bg-[var(--rf-blu-25)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--rf-blu-800)]">
                            Big one
                        </span>
                    )}
                    <span
                        className={cn(
                            'rounded-[2px] px-1.5 py-0.5 text-[11px] font-medium capitalize',
                            SEASON_STYLES[primarySeason],
                        )}
                    >
                        {faire.seasons.join(' & ')}
                    </span>
                </div>
                <div className="mt-0.5 text-[13px] text-[var(--rf-fg-2)]">
                    {faire.venue ? `${faire.venue} · ` : ''}
                    {faire.city}, {faire.state}
                    {faire.distanceMiles !== null && (
                        <span className="text-[var(--rf-fg-3)]">
                            {' '}
                            · {formatDistance(faire.distanceMiles)}
                        </span>
                    )}
                </div>
                {/*
                 * `items-start` with a non-wrapping icon: several date lines run
                 * long enough to wrap ("Aug 29 – Oct 25 (+ Mon Sep 7, Oct 12)"),
                 * and a wrapping flex row left the calendar stranded on its own
                 * line above the text.
                 */}
                <div
                    className={cn(
                        'mt-2 flex items-start gap-1.5 text-sm',
                        TONE_COLORS[faire.schedule.tone],
                    )}
                >
                    <CalendarDays
                        className="mt-[3px] h-[15px] w-[15px] flex-none opacity-55"
                        aria-hidden="true"
                    />
                    <span>
                        {faire.schedule.dateLine}
                        {isTbd && (
                            <span className="ml-1.5 inline-block rounded-[2px] bg-[var(--rf-warning-light)] px-1.5 py-px align-[2px] text-[11px] font-medium text-[var(--rf-warning)]">
                                TBD
                            </span>
                        )}
                    </span>
                </div>
                <div className="mt-1 flex items-start gap-1.5 text-[13px] text-[var(--rf-fg-2)]">
                    <Bus
                        className="mt-[3px] h-[15px] w-[15px] flex-none opacity-55"
                        aria-hidden="true"
                    />
                    <span>Nearest city: {faire.nearestCity}</span>
                </div>
            </button>
            <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-3">
                <a
                    href={faire.tripUrl}
                    className="inline-flex h-9 items-center rounded-[3px] bg-[var(--rf-primary)] px-3.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-[var(--rf-primary-hover)]"
                >
                    Get to&nbsp;{faire.nearestCityShort}&nbsp;→
                </a>
                {faire.url && (
                    <a
                        href={faire.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[13px] font-semibold text-[var(--rf-fg-2)] hover:underline"
                    >
                        Official site ↗
                    </a>
                )}
                <span className="text-xs text-[var(--rf-fg-3)]">
                    {CONFIDENCE_LABELS[faire.dateConfidence]}
                </span>
            </div>
        </li>
    )
}
